/**
 * **Template struk** — kepala & kaki yang dilihat tamu (Imron, 2026-08-07).
 *
 * Imron: *"bikin UI untuk template creation-nya, seperti LOGO, alamat1, alamat2, footnote atau
 * wording yang bisa diganti. Bisa di-preview."*
 *
 * ### ⛔⛔⛔ Pratinjaunya LAHIR DARI RENDERER YANG SAMA
 * `renderReceipt` → `toPreview`, kode yang sama persis dengan yang mengirim byte ke printer.
 * Menyusun pratinjau sendiri di layar berarti **renderer kedua**, dan ia akan menyimpang pada
 * perubahan pertama: pemotongan `..`, alih-aksara ASCII (`—` → `-`), lebar 32 vs 48 kolom. Yang
 * melihat selisihnya adalah tamu — bukan pemilik yang tadi menekan Simpan sambil menatap pratinjau
 * yang terlihat benar.
 *
 * ### ⚠️ Logo tergantung LEBAR KERTAS, dan itu dinyatakan di layar
 * Yang tersimpan raster 1-bit, bukan PNG — printer termal tak bisa mendekode gambar. Konsekuensinya
 * jujur: mengganti lebar kertas **tidak** menskalakan logo yang sudah tersimpan (menskalakan raster
 * 1-bit menghasilkan bintik). ⇒ pilihan lebar kertas ada DI ATAS tombol unggah, bukan di sudut
 * pratinjau, supaya ia terbaca sebagai keputusan — bukan tombol lihat-lihat.
 */
import { Alert, Button, Card, Input, Segmented, Select, Space, Typography, Upload, message } from 'antd';
import type { UploadProps } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DOTS, decodeLogoRaster, encodeLogoRaster, isReceiptFont, isReceiptScale } from '@osbpos/contract';
import type { PaperWidth, PrinterProfile, ReceiptFont, ReceiptScale } from '@osbpos/contract';
import { renderReceipt, toPreview } from '@osbpos/domain';
import type { Receipt as ReceiptDoc } from '@osbpos/domain';

import { api } from '../api/client';
import type { Outlet } from '../api/client';
import { canWrite } from '../api/session';

/** Tinggi maksimum logo dalam titik — ±2,5 cm kertas. Lebih dari itu memakan struk, bukan menghias. */
const MAX_LOGO_DOTS = 200;

interface Setelan {
  readonly id?: string;
  receiptAddress1: string;
  receiptAddress2: string;
  /** ⚠️ EMPAT baris kaki terpisah — lihat `ReceiptTemplate` di `@osbpos/domain`. */
  receiptFootnote: string;
  receiptFootnote2: string;
  receiptFootnote3: string;
  receiptFootnote4: string;
  receiptLogoRaster: string | null;
  receiptFont: ReceiptFont;
  receiptNameScale: ReceiptScale;
}

/**
 * ⚠️ Bawaan yang sudah BENAR, bukan kosong (Imron, 2026-08-25: *"berikan nilai default untuk yang
 * malas"*). Struk termal Indonesia hampir selalu menampilkan nama tokonya besar ⇒ `large`.
 *
 * ⛔ Yang TIDAK diberi bawaan: teks alamat & kaki. Mengisinya sendiri berarti struk mencetak
 * kalimat yang tak pernah ditulis pemiliknya — dan aturan Imron sendiri berbunyi *"jika kosong
 * jangan di print"*. Yang disediakan untuk yang malas adalah tombol **Isi contoh**, bukan diam-diam.
 */
const KOSONG: Setelan = {
  receiptAddress1: '', receiptAddress2: '',
  receiptFootnote: '', receiptFootnote2: '', receiptFootnote3: '', receiptFootnote4: '',
  receiptLogoRaster: null,
  receiptFont: 'normal', receiptNameScale: 'normal',
};

/** Contoh kaki struk yang lazim di F&B Indonesia — dipasang HANYA saat tombolnya ditekan. */
const CONTOH_KAKI = ['Terima kasih atas kunjungan Anda', 'Barang yang sudah dibeli tidak dapat ditukar'] as const;

/**
 * Mengubah berkas gambar jadi raster 1-bit selebar kertas.
 *
 * ⛔ Terjadi di PERAMBAN, sekali, saat diunggah — bukan di server. Kalau yang disimpan PNG, ada
 * yang harus mendekodenya, dan tempat yang paling mungkin adalah portal-api: satu pustaka gambar
 * masuk ke server demi satu fitur cetak.
 *
 * ⚠️ Ambangnya luminansi 0.5 tanpa dithering. Dithering membuat foto terlihat lebih baik dan **logo
 * terlihat lebih buruk** — garis tipis pecah jadi bintik pada printer 203 dpi. Logo yang bagus di
 * struk adalah logo yang sudah hitam-putih tegas sejak asalnya.
 */
async function keRaster(file: File, paper: PaperWidth): Promise<string> {
  const bitmap = await createImageBitmap(file);
  const target = DOTS[paper];
  /* ⚠️ Tak pernah DIPERBESAR: memperbesar raster 1-bit cuma menggandakan titik jadi tangga. */
  const w = Math.min(bitmap.width, target);
  const h = Math.min(Math.round((bitmap.height * w) / bitmap.width), MAX_LOGO_DOTS);
  /* ⭐ Lebar dibulatkan ke kelipatan 8: `GS v 0` menghitung BYTE per baris, jadi sisa bit di byte
     terakhir akan tercetak sebagai garis hitam kalau tak sengaja dibiarkan menyala. */
  const w8 = Math.max(8, Math.floor(w / 8) * 8);

  const canvas = document.createElement('canvas');
  canvas.width = w8;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (ctx === null) throw new Error('canvas 2d tak tersedia');
  /* Latar PUTIH dulu: PNG transparan tanpa ini jadi hitam pekat seluruhnya. */
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, w8, h);
  ctx.drawImage(bitmap, 0, 0, w8, h);

  const px = ctx.getImageData(0, 0, w8, h).data;
  const perRow = w8 / 8;
  const bits = new Uint8Array(perRow * h);
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w8; x += 1) {
      const i = (y * w8 + x) * 4;
      const lum = 0.299 * (px[i] ?? 0) + 0.587 * (px[i + 1] ?? 0) + 0.114 * (px[i + 2] ?? 0);
      /* bit 1 = titik HITAM — konvensi `GS v 0`, jadi tak ada pembalikan di renderer. */
      if (lum < 128) {
        const idx = y * perRow + (x >> 3);
        bits[idx] = (bits[idx] ?? 0) | (0x80 >> (x & 7));
      }
    }
  }
  return encodeLogoRaster({ width: w8, height: h, bits });
}

/** Contoh isi struk untuk pratinjau. ⚠️ Angkanya karangan — yang diuji tata letaknya, bukan uangnya. */
function contoh(outletName: string, s: Setelan): ReceiptDoc {
  return {
    outletName,
    template: {
      ...(s.receiptAddress1 === '' ? {} : { address1: s.receiptAddress1 }),
      ...(s.receiptAddress2 === '' ? {} : { address2: s.receiptAddress2 }),
      ...(s.receiptFootnote === '' ? {} : { footnote: s.receiptFootnote }),
      ...(s.receiptFootnote2 === '' ? {} : { footnote2: s.receiptFootnote2 }),
      ...(s.receiptFootnote3 === '' ? {} : { footnote3: s.receiptFootnote3 }),
      ...(s.receiptFootnote4 === '' ? {} : { footnote4: s.receiptFootnote4 }),
      ...(s.receiptLogoRaster === null ? {} : { logoRaster: s.receiptLogoRaster }),
      font: s.receiptFont,
      nameScale: s.receiptNameScale,
    },
    orderNo: 'SMB/2026-08-07/000137',
    printedAt: '2026-08-07 19:42',
    businessDate: '2026-08-07',
    cashier: 'Rani',
    lines: [
      { name: 'Nasi Goreng Kampung', qty: '2 x 45.000', amount: '90000.0000' },
      { name: 'Es Teh Manis', qty: '1 x 12.000', amount: '12000.0000' },
    ],
    subtotal: '102000.0000',
    discount: '0.0000',
    taxes: [
      { name: 'Service Charge', amount: '5100.0000' },
      { name: 'PB1', amount: '10710.0000' },
    ],
    grandTotal: '117810.0000',
    tenders: [{ name: 'Tunai', amount: '150000.0000', opensDrawer: true }],
    change: '32190.0000',
  };
}

/** ⛔ Kosong disimpan sebagai NULL, bukan `''` — "tak dinyatakan" harus bisa dibedakan dari "sengaja kosong". */
function kosongJadiNull(v: string): string | null {
  return v.trim() === '' ? null : v.trim();
}

export function ReceiptTemplate() {
  const { t } = useTranslation();
  const boleh = canWrite();
  const [outlets, setOutlets] = useState<readonly Outlet[]>([]);
  const [outletId, setOutletId] = useState<string | undefined>(undefined);
  const [rows, setRows] = useState<readonly Record<string, unknown>[]>([]);
  const [draft, setDraft] = useState<Setelan>(KOSONG);
  const [paper, setPaper] = useState<PaperWidth>(80);
  const [busy, setBusy] = useState(false);

  const muat = useCallback(async () => {
    const [o, s] = await Promise.all([api.outlets(), api.outletSettings()]);
    setOutlets(o);
    setRows(s);
    setOutletId((cur) => cur ?? o[0]?.id);
  }, []);

  useEffect(() => { void muat().catch(() => undefined); }, [muat]);

  /* ⚠️ Barisnya bisa BELUM ADA — outlet yang tak pernah disetel apa pun memang tak punya. */
  useEffect(() => {
    const row = rows.find((r) => r['outlet_id'] === outletId);
    setDraft({
      ...(row?.['id'] === undefined ? {} : { id: String(row['id']) }),
      receiptAddress1: String(row?.['receipt_address_1'] ?? ''),
      receiptAddress2: String(row?.['receipt_address_2'] ?? ''),
      receiptFootnote: String(row?.['receipt_footnote'] ?? ''),
      receiptFootnote2: String(row?.['receipt_footnote_2'] ?? ''),
      receiptFootnote3: String(row?.['receipt_footnote_3'] ?? ''),
      receiptFootnote4: String(row?.['receipt_footnote_4'] ?? ''),
      /* ⚠️ Outlet yang belum punya baris setelan sama sekali tetap dapat bawaan yang benar. */
      receiptFont: isReceiptFont(row?.['receipt_font']) ? row['receipt_font'] : 'normal',
      receiptNameScale: isReceiptScale(row?.['receipt_name_scale'])
        ? row['receipt_name_scale'] : 'normal',
      receiptLogoRaster: row?.['receipt_logo_raster'] === null
        || row?.['receipt_logo_raster'] === undefined
        ? null
        : String(row['receipt_logo_raster']),
    });
  }, [rows, outletId]);

  const outletName = outlets.find((o) => o.id === outletId)?.name ?? 'OUTLET';

  /*
   * ⛔⛔ **Logo yang terlalu lebar DILEWATI renderer, diam-diam.** `escpos.ts` menolak raster yang
   * lebih lebar dari kertas (mencetaknya menghasilkan baris yang terlipat jadi bubur), dan caranya
   * menolak adalah **tidak mencetak apa pun**. Tanpa peringatan ini, pemilik yang mengunggah logo
   * saat 80 mm lalu punya printer 58 mm melihat pratinjau tanpa logo dan menyimpulkan "logonya
   * belum kesimpan" — padahal tersimpan, cuma tak akan pernah keluar. ⇒ katakan, dan sebutkan
   * obatnya (unggah ulang pada lebar ini).
   */
  const logoTerlaluLebar = useMemo(() => {
    const l = decodeLogoRaster(draft.receiptLogoRaster);
    return l !== undefined && l.width > DOTS[paper];
  }, [draft.receiptLogoRaster, paper]);

  const pratinjau = useMemo(() => {
    const profil: PrinterProfile = {
      address: { transport: 'net', host: '0.0.0.0', port: 9100 },
      paperWidth: paper,
      codepage: 0,
      hasCutter: true,
      hasDrawerKick: true,
      model: 'pratinjau',
    };
    try {
      return toPreview(renderReceipt(contoh(outletName, draft), profil));
    } catch (e: unknown) {
      /* ⛔ Pratinjau yang meledak tak boleh mematikan layarnya — pemilik masih harus bisa
         memperbaiki isian yang menyebabkannya. */
      return String(e);
    }
  }, [draft, outletName, paper]);

  const unggah: UploadProps = {
    accept: 'image/*',
    showUploadList: false,
    beforeUpload: (file) => {
      void keRaster(file as unknown as File, paper)
        .then((raster) => { setDraft((d) => ({ ...d, receiptLogoRaster: raster })); })
        .catch((e: unknown) => {
          void message.error(e instanceof Error ? e.message : String(e));
        });
      return false; // ⛔ Tak pernah diunggah ke server sebagai berkas — yang dikirim rasternya.
    },
  };

  const simpan = async () => {
    if (outletId === undefined) return;
    setBusy(true);
    try {
      const body = {
        receiptAddress1: draft.receiptAddress1.trim() === '' ? null : draft.receiptAddress1.trim(),
        receiptAddress2: draft.receiptAddress2.trim() === '' ? null : draft.receiptAddress2.trim(),
        receiptFootnote: kosongJadiNull(draft.receiptFootnote),
        receiptFootnote2: kosongJadiNull(draft.receiptFootnote2),
        receiptFootnote3: kosongJadiNull(draft.receiptFootnote3),
        receiptFootnote4: kosongJadiNull(draft.receiptFootnote4),
        receiptLogoRaster: draft.receiptLogoRaster,
        receiptFont: draft.receiptFont,
        receiptNameScale: draft.receiptNameScale,
      };
      if (draft.id === undefined) await api.createOutletSettings({ outletId, ...body });
      else await api.updateMaster('outlet-settings', draft.id, body);
      void message.success(t('receipt.saved'));
      setRows(await api.outletSettings());
    } catch (e: unknown) {
      void message.error(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }} data-testid="receipt">
      <Alert type="info" showIcon data-testid="receipt-hint" title={t('receipt.hint')} />

      <div className="receipt-editor">
        <Card size="small" title={t('receipt.title')} style={{ flex: '1 1 340px', minWidth: 320 }}>
          <Space orientation="vertical" style={{ width: '100%' }} size="small">
            <Select
              style={{ width: '100%' }}
              value={outletId}
              data-testid="receipt-outlet"
              placeholder={t('receipt.pickOutlet')}
              onChange={setOutletId}
              options={outlets.map((o) => ({ value: o.id, label: `${o.code} · ${o.name}` }))}
            />

            {/*
              ⚠️ Lebar kertas DI ATAS tombol unggah, bukan di sudut pratinjau: logo dikonversi
              menurut lebar yang aktif SAAT diunggah, dan mengubahnya belakangan tak menskalakan
              apa pun. Ia keputusan, bukan tombol lihat-lihat.
            */}
            <Segmented
              block
              value={paper}
              data-testid="receipt-paper"
              onChange={(v) => { setPaper(v as PaperWidth); }}
              options={[{ value: 58, label: '58 mm' }, { value: 80, label: '80 mm' }]}
            />

            <Space wrap>
              <Upload {...unggah} disabled={!boleh}>
                <Button data-testid="receipt-logo-upload" disabled={!boleh}>
                  {t('receipt.uploadLogo')}
                </Button>
              </Upload>
              {draft.receiptLogoRaster !== null && (
                <Button
                  danger
                  data-testid="receipt-logo-hapus"
                  disabled={!boleh}
                  onClick={() => { setDraft((d) => ({ ...d, receiptLogoRaster: null })); }}
                >
                  {t('receipt.removeLogo')}
                </Button>
              )}
            </Space>
            <Typography.Text type="secondary">{t('receipt.logoHint')}</Typography.Text>
            {logoTerlaluLebar && (
              <Alert
                type="warning"
                showIcon
                data-testid="receipt-logo-lebar"
                title={t('receipt.logoTooWide', { mm: paper })}
              />
            )}

            <Input
              data-testid="receipt-alamat1"
              maxLength={120}
              disabled={!boleh}
              placeholder={t('receipt.address1')}
              value={draft.receiptAddress1}
              onChange={(e) => { setDraft((d) => ({ ...d, receiptAddress1: e.target.value })); }}
            />
            <Input
              data-testid="receipt-alamat2"
              maxLength={120}
              disabled={!boleh}
              placeholder={t('receipt.address2')}
              value={draft.receiptAddress2}
              onChange={(e) => { setDraft((d) => ({ ...d, receiptAddress2: e.target.value })); }}
            />
            {/*
              ⭐⭐ **Empat baris kaki, satu kotak per baris** — bukan satu textarea.
              ⛔ Textarea membiarkan orang menulis paragraf, dan paragraf di kertas 32 kolom pecah
              di tempat yang tak seorang pun pilih. Satu kotak per baris membuat batasnya TERLIHAT
              saat mengetik, dan pratinjau di sebelah menunjukkan akibatnya seketika.
            */}
            <Typography.Text strong>{t('receipt.footnoteTitle')}</Typography.Text>
            {([1, 2, 3, 4] as const).map((n) => {
              const key = (n === 1 ? 'receiptFootnote' : `receiptFootnote${String(n)}`) as
                'receiptFootnote' | 'receiptFootnote2' | 'receiptFootnote3' | 'receiptFootnote4';
              return (
                <Input
                  key={n}
                  data-testid={`receipt-footnote-${n}`}
                  maxLength={60}
                  disabled={!boleh}
                  placeholder={t('receipt.footnoteN', { n })}
                  value={draft[key]}
                  onChange={(e) => {
                    const v = e.target.value;
                    setDraft((d) => ({ ...d, [key]: v }));
                  }}
                />
              );
            })}
            {/* ⭐ "Nilai default untuk yang malas" — DITEKAN, bukan diam-diam terisi. */}
            {boleh && (
              <Button
                size="small"
                data-testid="receipt-contoh"
                onClick={() => {
                  setDraft((d) => ({
                    ...d,
                    receiptFootnote: d.receiptFootnote === '' ? CONTOH_KAKI[0] : d.receiptFootnote,
                    receiptFootnote2: d.receiptFootnote2 === '' ? CONTOH_KAKI[1] : d.receiptFootnote2,
                  }));
                }}
              >
                {t('receipt.fillExample')}
              </Button>
            )}

            {/*
              ⭐ Ukuran huruf. ⚠️ DUA nilai, bukan angka bebas: printer termal cuma punya Font A dan
              Font B. Menawarkan "ukuran 11" berarti setelan yang berbohong kepada yang menyetelnya.
              ⚠️ `small` bukan sekadar huruf kecil — ia menambah KOLOM (58 mm: 32 → 42), dan itulah
              yang membuatnya berguna untuk nama menu yang panjang.
            */}
            <Typography.Text strong>{t('receipt.fontTitle')}</Typography.Text>
            <Segmented
              block
              value={draft.receiptFont}
              data-testid="receipt-font"
              disabled={!boleh}
              onChange={(v) => { setDraft((d) => ({ ...d, receiptFont: v as ReceiptFont })); }}
              options={[
                { value: 'normal', label: t('receipt.fontNormal') },
                { value: 'small', label: t('receipt.fontSmall') },
              ]}
            />
            <Typography.Text strong>{t('receipt.nameScaleTitle')}</Typography.Text>
            <Segmented
              block
              value={draft.receiptNameScale}
              data-testid="receipt-name-scale"
              disabled={!boleh}
              onChange={(v) => { setDraft((d) => ({ ...d, receiptNameScale: v as ReceiptScale })); }}
              options={[
                { value: 'normal', label: t('receipt.scaleNormal') },
                { value: 'large', label: t('receipt.scaleLarge') },
              ]}
            />

            {boleh && (
              <Button
                type="primary"
                block
                loading={busy}
                data-testid="receipt-simpan"
                disabled={outletId === undefined}
                onClick={() => void simpan()}
              >
                {t('receipt.save')}
              </Button>
            )}
          </Space>
        </Card>

        {/*
          ⭐ Pratinjau bersebelahan, bukan di bawah: yang diketik dan akibatnya harus terlihat
          BERSAMAAN — kalau harus menggulir, orang mengetik dulu semuanya lalu baru melihat, dan
          pemotongan `..` baru ketahuan sesudah tiga kolom terlanjur disusun.
        */}
        <Card
          size="small"
          title={t('receipt.preview', { mm: paper })}
          style={{ flex: '1 1 380px', minWidth: 320 }}
        >
          <pre className="receipt-preview" data-testid="receipt-preview">{pratinjau}</pre>
          <Typography.Text type="secondary">{t('receipt.previewHint')}</Typography.Text>
        </Card>
      </div>
    </Space>
  );
}
